<script setup lang="ts">
import { computed } from "vue"
import { JIcon, JLabel, JPopover, JButton } from '@/components/atoms'
import type { ButtonVariants } from '@/components/shadcn'
import { cn } from "@/lib/utils"

export type TitlebarButton = {
  /** 버튼 아이콘 */
  icon?: string
  /** 버튼 텍스트 */
  text?: string
  /** 버튼 클릭 핸들러 */
  onClick?: () => void
  /** 버튼 variant */
  variant?: ButtonVariants['variant']
  /** 버튼 스타일 타입 */
  styletype?: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'outline' | 'ghost' | 'link' | 'sm' | 'lg' | 'icon'
  /** 버튼 size */
  size?: 'sm' | 'md' | 'lg'
  /** 버튼 비활성화 */
  disabled?: boolean
  /** 버튼 로딩 상태 */
  loading?: boolean
}

type StyleType =
  | 'default'    // 기본 스타일: 일반 페이지용 (흰 배경, 얇은 보더)
  | 'primary'    // 강조 스타일: 주요 기능용 (밝은 파란 배경, 흰 텍스트)
  | 'accent'     // 액센트 스타일: 부드러운 강조용 (연한 파란 배경, 진한 텍스트)
  | 'neutral'    // 중립 스타일: 서브 페이지용 (회색 배경, 회색 텍스트)
  | 'elevated'   // 강조 스타일: 깊이감 있는 구분 (흰 배경, 그림자, 보더)

const props = withDefaults(
  defineProps<{
    /** Titlebar 스타일 타입 */
    styletype?: StyleType
    /** 프로그램 아이콘 */
    icon?: string
    /** 프로그램명 */
    title?: string
    /** 프로그램 설명 (Popover에 표시) */
    description?: string
    /** 도움말 아이콘(?) 표시 여부 — 클릭 시 help 이벤트 emit */
    showHelp?: boolean
    /** 메인 버튼 목록 */
    buttons?: TitlebarButton[]
    /** 추가 클래스 (외부 커스터마이징용) */
    class?: string
  }>(),
  {
    styletype: 'default',
    buttons: () => [],
  }
)

/**
 * styletype -> class 매핑 (배경, 보더, 그림자 등)
 */
const STYLE_PRESETS: Record<StyleType, { 
  class: string
  iconClass: string      // 아이콘 색상 클래스
  titleClass: string     // 제목 텍스트 색상 클래스
  infoIconClass: string  // 정보 아이콘 색상 클래스
}> = {
  default: {
    // 기본: 흰 배경 + 얇은 보더 (가장 일반적)
    class: 'flex items-center justify-between w-full h-9 px-3 border-b border-border bg-background',
    iconClass: 'text-primary',
    titleClass: 'text-foreground text-md',
    infoIconClass: 'text-muted-foreground hover:text-primary',
  },
  primary: {
    // 강조: 밝은 파란 배경 + 흰 텍스트 (명확한 강조)
    // text-white를 제거하여 버튼이 색상 상속을 받지 않도록 함
    class: 'flex items-center justify-between w-full h-9 px-3 border-b border-blue-400/30 bg-blue-500',
    iconClass: 'text-white',
    titleClass: 'text-white font-semibold text-md',
    infoIconClass: 'text-white/80 hover:text-white',
  },
  accent: {
    // 액센트: 연한 파란 배경 + 진한 파란 텍스트 (부드러운 강조)
    // text-blue-700을 제거하여 버튼이 색상 상속을 받지 않도록 함
    class: 'flex items-center justify-between w-full h-9 px-3 border-b border-blue-200 bg-blue-50',
    iconClass: 'text-blue-600',
    titleClass: 'text-blue-700 font-semibold text-md',
    infoIconClass: 'text-blue-600/70 hover:text-blue-700',
  },
  neutral: {
    // 중립: 회색 배경 + 회색 텍스트 (확실한 구분)
    // text-gray-700을 제거하여 버튼이 색상 상속을 받지 않도록 함
    class: 'flex items-center justify-between w-full h-9 px-3 border-b border-gray-300 bg-gray-100',
    iconClass: 'text-gray-600',
    titleClass: 'text-gray-700 text-md',
    infoIconClass: 'text-gray-500 hover:text-gray-700',
  },
  elevated: {
    // 강조: 흰 배경 + 그림자 + 보더 (깊이감 있는 구분)
    class: 'flex items-center justify-between w-full h-9 px-3 border-b border-border bg-background shadow-md',
    iconClass: 'text-primary',
    titleClass: 'text-foreground font-semibold text-md',
    infoIconClass: 'text-muted-foreground hover:text-primary',
  },
}

const preset = computed(() => {
  return STYLE_PRESETS[props.styletype] ?? STYLE_PRESETS.default
})

const emit = defineEmits<{
  /** 버튼 클릭 이벤트 */
  buttonClick: [button: TitlebarButton]
  /** 도움말 아이콘 클릭 이벤트 */
  help: []
}>()

const handleButtonClick = (button: TitlebarButton) => {
  // 버튼 클릭 핸들러 패턴:
  // 1. button.onClick이 있으면 실행 (인라인 핸들러)
  // 2. 항상 emit('buttonClick', button) 실행 (부모 컴포넌트로 전달)
  // 주의: onClick과 emit이 모두 실행되므로 중복 처리 가능성 있음
  button.onClick?.()
  emit('buttonClick', button)
}
</script>

<template>
  <div :class="cn(preset.class, props.class)">
    <!-- 왼쪽: 아이콘 + 프로그램명 -->
    <div class="flex items-center gap-3 flex-1">
      <!-- 아이콘 -->
      <JIcon 
        v-if="icon" 
        :name="icon" 
        :class="preset.iconClass"
      />
      
      <!-- 프로그램명 + 정보 아이콘 + Popover -->
      <div class="flex items-center gap-2">
        <!-- 프로그램명 -->
        <JLabel 
          :text="title || ''" 
          :class="cn('font-semibold', preset.titleClass)"
        />
        
        <!-- 정보 아이콘 (description이 있을 때만 표시) -->
        <JPopover 
          v-if="description" 
          position="bottom"
          align="center"
          :side-offset="8"
        >
          <template #trigger>
            <JIcon 
              name="info" 
              size="sm" 
              :class="cn('cursor-help transition-colors inline-flex', preset.infoIconClass)"
            />
          </template>
          <div class="p-2">
            <p class="text-xs text-muted-foreground whitespace-normal break-words">{{ description }}</p>
          </div>
        </JPopover>

        <!-- 도움말 아이콘 (showHelp일 때 표시, 클릭 시 help 이벤트) -->
        <JIcon
          v-if="showHelp"
          name="circleQuestionMark"
          size="sm"
          :class="cn('cursor-pointer transition-colors inline-flex', preset.infoIconClass)"
          @click="emit('help')"
        />
      </div>
    </div>

    <!-- 오른쪽: 메인 버튼들 -->
    <!-- buttons prop으로 정의된 버튼들 (v-if로 조건부 렌더링) -->
    <div v-if="buttons && buttons.length > 0" class="flex items-center gap-2">
      <JButton
        v-for="(button, index) in buttons"
        :key="index"
        :variant="button.variant"
        :styletype="button.styletype"
        :disabled="button.disabled"
        :loading="button.loading"
        :size="button.size"
        @click="handleButtonClick(button)"
      >
        <JIcon v-if="button.icon" :name="button.icon" size="sm" class="mr-1.5" />
        <span v-if="button.text">{{ button.text }}</span>
      </JButton>
    </div>

    <!-- 버튼 슬롯 레이아웃 설명:
         - buttons prop과 buttons slot이 모두 제공되면 함께 표시됨
         - buttons prop: 구조화된 버튼 정의 (JButton 컴포넌트 사용)
         - buttons slot: 완전한 커스텀 버튼 제어 가능
         - 레이아웃: buttons prop이 먼저 렌더링되고, 그 다음 slot이 렌더링됨 -->
    <slot name="buttons" />
  </div>
</template>

